/**
 * Sidebar for the HERO "Dokumente" module (220px). Title + search field, an
 * "Eingang" group (Postkorb), the active "Alle Dokumente" folder, and the
 * category list. 1:1 with prototype L370.
 */
import {
  ClipboardList,
  FileText,
  Folder,
  Inbox,
  Receipt,
  ScrollText,
  Search,
  Shield,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H, SIDEBAR_WIDTH } from "../../hero-tokens";
import { HeroClickPulseDot } from "./hero-click-pulse-dot";
import { DOCUMENT_COUNT } from "./module-dokumente-data";

const monoLabel: CSSProperties = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  color: H.inkGhost,
  textTransform: "uppercase",
};

const sidebarRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 9px",
  borderRadius: 8,
  fontSize: 13.5,
  color: H.inkSoft,
};

export type DokumenteSidebarSelection = "postkorb" | "alle";

const neuBadge: CSSProperties = {
  fontFamily: H.label,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: H.accent,
  background: H.accentSoft,
  padding: "2px 6px",
  borderRadius: 999,
};

function SideNavButton(props: {
  readonly active: boolean;
  readonly icon: ReactNode;
  readonly iconColor: string;
  readonly label: string;
  readonly count?: number;
  readonly badge?: string;
  readonly showClickPulse?: boolean;
  readonly onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        appearance: "none",
        border: "none",
        fontFamily: H.sans,
        width: "100%",
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 9px",
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: props.active ? 600 : 400,
        color: props.active ? H.accent : H.inkSoft,
        background: props.active ? H.accentSoft : "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ color: props.iconColor, display: "inline-flex" }}>
        {props.icon}
      </span>
      <span style={{ flex: 1 }}>{props.label}</span>
      {props.badge !== undefined ? (
        <span style={neuBadge}>{props.badge}</span>
      ) : props.count !== undefined ? (
        <span style={{ fontSize: 12, color: H.inkGhost }}>{props.count}</span>
      ) : null}
      {props.showClickPulse === true ? (
        <HeroClickPulseDot
          placement={props.badge !== undefined ? "nav-before-badge" : "nav-end"}
        />
      ) : null}
    </button>
  );
}

function SideItem(props: {
  readonly icon: ReactNode;
  readonly iconColor: string;
  readonly label: string;
}): ReactNode {
  return (
    <div style={sidebarRow}>
      <span style={{ color: props.iconColor, display: "inline-flex" }}>
        {props.icon}
      </span>
      <span style={{ flex: 1 }}>{props.label}</span>
    </div>
  );
}

export function DokumenteSidebar(props: {
  readonly selection: DokumenteSidebarSelection;
  readonly showPostkorbPulse?: boolean;
  readonly onSelectionChange: (selection: DokumenteSidebarSelection) => void;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteSidebar");
  return (
    <aside
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: "1px solid #EEF0F3",
        background: "#FBFBFC",
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", padding: "0 6px 12px" }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color: H.ink }}>
          {t("dokumente")}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 34,
          padding: "0 11px",
          background: "#F1F2F4",
          borderRadius: 9,
          color: H.inkDim,
          fontSize: 13,
          marginBottom: 14,
        }}
      >
        <Search width={15} height={15} strokeWidth={1.8} />
        {t("suchen")}
      </div>

      <div style={{ ...monoLabel, padding: "4px 9px 8px" }}>{t("eingang")}</div>
      <SideNavButton
        active={props.selection === "postkorb"}
        icon={<Inbox width={16} height={16} strokeWidth={1.8} />}
        iconColor="#6B7280"
        label={t("postkorb")}
        badge={t("neu")}
        showClickPulse={props.showPostkorbPulse === true}
        onClick={() => {
          props.onSelectionChange("postkorb");
        }}
      />

      <div style={{ ...monoLabel, padding: "18px 9px 8px" }}>
        {t("dokumente")}
      </div>
      <SideNavButton
        active={props.selection === "alle"}
        icon={<Folder width={16} height={16} strokeWidth={1.9} />}
        iconColor={H.accent}
        label={t("alleDokumente")}
        count={DOCUMENT_COUNT}
        onClick={() => {
          props.onSelectionChange("alle");
        }}
      />

      <div style={{ ...monoLabel, padding: "18px 9px 8px" }}>
        {t("filteransichten")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SideItem
          icon={<Receipt width={16} height={16} strokeWidth={1.8} />}
          iconColor="#4C8C6B"
          label={t("rechnungen")}
        />
        <SideItem
          icon={<FileText width={16} height={16} strokeWidth={1.8} />}
          iconColor="#4F77AE"
          label={t("vertraege")}
        />
        <SideItem
          icon={<ScrollText width={16} height={16} strokeWidth={1.8} />}
          iconColor="#A8824B"
          label={t("teilungserklaerungen")}
        />
        <SideItem
          icon={<ClipboardList width={16} height={16} strokeWidth={1.8} />}
          iconColor="#7A6CB0"
          label={t("protokolle")}
        />
        <SideItem
          icon={<Shield width={16} height={16} strokeWidth={1.8} />}
          iconColor="#B0703E"
          label={t("versicherungen")}
        />
      </div>
    </aside>
  );
}
